import { JsonSchemaRoot } from "components/JsonSchema/types";
import BaseEntity from "./BaseEntity";
import ClassifierCategory from "./ClassifierCategoryId";
import ClassifierGroup from "./ClassifierGroup";
import ClassifierImplementation from "./ClassifierImplementationModel";
import { ClassifierResultAnswerTag } from "./ClassifierResultAnswerTag";

export default interface Classifier extends BaseEntity {
    name: string;
    description: string;
    classifierIdentifier: number;
    organizationId: string;
    classifierImplementations: ClassifierImplementation[];
    classifierResultAnswerTags: ClassifierResultAnswerTag[];
    isPublished: boolean;
    expiresOn: string;
    classifierTypeName: ClassifierType;
    classifierOutputs: ClassifierOutput[];
    classifierCategory: ClassifierCategory;
    classifierGroup: ClassifierGroup;
    isIndexed: boolean;
    isGroupable: boolean;
    resultType: ResultType;
}

export interface ClassifierOutput extends BaseEntity {
    classifierId: string;
    result: string;
}

export interface ClassifierForLibrary {
    id: string;
    name: string;
    description: string;
    groupId: string;
    groupName: string;
    existsInTenant: boolean;
}

export type ClassifierLibrary = Record<string, ClassifierForLibrary[]>;

export interface AddClassifierGroupResult {
    successfulClassifierNames: string[];
    failedClassifierNames: string[];
}

export interface CreateClassifierReturn {
    classifier: Classifier;
    classifierId: string;
    createdBy: string;
    createdOn: string;
    modifiedBy: string;
    modifiedOn: string;
    id: string;
    isActive: boolean;
    queryString: string;
    threshold: number;
    versionNumber: number;
}

export enum ResultType {
    StringType,
    NumberType,
    JsonType,
}

export const resultTypeToLabel: Record<ResultType, string> = {
    [ResultType.StringType]: "String",
    [ResultType.NumberType]: "Number",
    [ResultType.JsonType]: "Json",
};

export const selectResultTypeOptions: Array<{
    label: string;
    value: ResultType;
}> = Object.values(ResultType)
    .filter((v) => typeof v === "number")
    .map((resultType: ResultType) => ({
        label: resultTypeToLabel[resultType],
        value: resultType,
    }));

export enum ClassifierType {
    Lucene = "Lucene",
    Metadata = "Metadata",
    ExtendedMetadata = "ExtendedMetadata",
    ExtendedMetadataObject = "ExtendedMetadataObject",
    Tensorflow = "Tensorflow",
    Database = "Database",
    LLM = "LLM",
}

export class ClassifierUtils {
    // converts a lucene search string to a list of search terms
    static searchStringToTerms(searchString?: string) {
        if (!searchString) {
            return [];
        }
        const split = searchString.match(/(?:[^\s"]+|"[^"]*")+/g);
        if (!split) return [];
        return split?.map((s) => s.replace(/"/g, ""));
    }
}

export type ExtendedMetadataClassifier = {
    id: string;
    description?: string;
    isIndexed: boolean;
    isGroupable: boolean;
    isPublished: boolean;
    isActive?: boolean;
    name: string;
    resultType: ResultType;
    /**
     * A valid json schema object if the `resultType` is `JsonType`
     */
    jsonSchema?: JsonSchemaRoot;
    classifierType?: ClassifierType;
};
